import {
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  SvgIcon,
  Tooltip,
} from "@mui/material"
import { useState } from "react"

import appIcons from "@utils/appIcons"
import { IconType } from "@utils/iconType"

// Built-in pills carry live counts: `count` is what the label shows right now
// (totals, or match counts while a search is live), `totalCount` sizes the
// number slot — so neither the count arriving nor the totals↔matches swap
// ever resizes a pill.
export type BuiltInPill = {
  id: string
  name: string
  count: number | null
  totalCount: number | null
}

// Custom views fold into an "N more" hover popover after this many pills —
// built-ins never fold: they are the page's tabs. House overflow rule:
// auxiliary content expands over the page, never shifts it.
const MAX_VISIBLE_CUSTOM_PILLS = 3

const MenuIcon = (props: { icon: IconType }) => (
  <SvgIcon
    component={props.icon}
    inheritViewBox
    sx={{ width: 16, height: 16, fill: "none", stroke: "currentColor" }}
  />
)

const focusRingSx = {
  "&.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: "2px",
  },
} as const

// The views row IS the page's one filtering frame: built-in pills are the old
// status tabs and the attention / my-students scopes in a single grammar,
// custom pills are saved predicate sets. Exactly one pill is active; its
// definition renders as the chip strip below. All view state lives with the
// page (the strip's drift actions share it) — this component only draws the
// row.
export const SavedViewsBar = (props: {
  builtIns: BuiltInPill[]
  customViews: { id: string; name: string }[]
  activeViewId: string | null
  // Drift dot on the active custom pill: built-ins never drift (editing their
  // definition simply re-matches the pills), saved views stay sticky and
  // report unsaved changes.
  isDrifted: boolean
  onSelectView: (viewId: string) => void
  onNewView: () => void
  onRenameView: (viewId: string) => void
  onDeleteView: (viewId: string) => void
}) => {
  const {
    builtIns,
    customViews,
    activeViewId,
    isDrifted,
    onSelectView,
    onNewView,
    onRenameView,
    onDeleteView,
  } = props

  // ---- pill kebab menu (custom views only: Rename · Delete) ----
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement
    viewId: string
  } | null>(null)

  // ---- overflow ----
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null)
  // Only custom pills fold; the active one always swaps into a visible slot.
  let visibleCustom = customViews.slice(0, MAX_VISIBLE_CUSTOM_PILLS)
  let overflowCustom = customViews.slice(MAX_VISIBLE_CUSTOM_PILLS)
  const activeOverflowIndex = overflowCustom.findIndex(
    (view) => view.id === activeViewId,
  )
  if (activeOverflowIndex >= 0) {
    const activePill = overflowCustom[activeOverflowIndex]
    const displaced = visibleCustom[visibleCustom.length - 1]
    visibleCustom = [...visibleCustom.slice(0, -1), activePill]
    overflowCustom = [
      displaced,
      ...overflowCustom.filter((view) => view.id !== activePill.id),
    ]
  }

  const renderBuiltInPill = (pill: BuiltInPill) => {
    const isActive = pill.id === activeViewId
    // The count slot renders from first paint ("· …") at a width sized for
    // the view's TOTAL count, so pills never resize when numbers land or when
    // search-match counts swap in.
    const slotCh = Math.max(5, 2 + String(pill.totalCount ?? 0).length)
    return (
      <Chip
        key={pill.id}
        data-testid="view-pill"
        label={
          <Box component="span" sx={{ display: "inline-flex" }}>
            {pill.name}
            <Box
              component="span"
              sx={{
                marginLeft: "4px",
                display: "inline-block",
                minWidth: `${slotCh}ch`,
                textAlign: "left",
                fontVariantNumeric: "tabular-nums",
                opacity: 0.72,
              }}
            >
              {`· ${pill.count ?? "…"}`}
            </Box>
          </Box>
        }
        clickable
        color={isActive ? "primary" : "default"}
        variant={isActive ? "filled" : "outlined"}
        onClick={() => onSelectView(pill.id)}
        sx={focusRingSx}
      />
    )
  }

  const renderCustomPill = (view: { id: string; name: string }) => {
    const isActive = view.id === activeViewId
    const showDot = isActive && isDrifted
    const chip = (
      <Chip
        key={view.id}
        data-testid="view-pill"
        label={
          showDot ? (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              {view.name}
              <Box
                component="span"
                data-testid="view-drift-dot"
                sx={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "currentColor",
                }}
              />
            </Box>
          ) : (
            view.name
          )
        }
        clickable
        color={isActive ? "primary" : "default"}
        variant={isActive ? "filled" : "outlined"}
        onClick={() => onSelectView(view.id)}
        onDelete={(event) =>
          setMenuState({ anchorEl: event.currentTarget, viewId: view.id })
        }
        deleteIcon={
          <SvgIcon
            component={appIcons.dotsHorizontal}
            inheritViewBox
            aria-label={`Open menu for ${view.name}`}
            sx={{
              width: "16px",
              height: "16px",
              fill: "none",
              stroke: "currentColor",
            }}
          />
        }
        sx={{
          // The kebab is VISIBLE at rest (quiet, not gone — hidden controls
          // are a discoverability bug, Mayer twice) and sharpens on hover.
          // Constant slot width, so pills never resize. MUI's delete-icon
          // slot pulls itself into the label with a -6px margin — undo that
          // so the ellipsis gets breathing room.
          "& .MuiChip-deleteIcon": {
            opacity: menuState?.viewId === view.id ? 1 : 0.4,
            transition: "opacity 0.15s",
            color: "inherit",
            marginLeft: "6px",
            marginRight: "8px",
          },
          "&:hover .MuiChip-deleteIcon, &:focus-within .MuiChip-deleteIcon, &.Mui-focusVisible .MuiChip-deleteIcon":
            {
              opacity: 1,
            },
          ...focusRingSx,
        }}
      />
    )
    return showDot ? (
      <Tooltip
        key={view.id}
        title="This view has unsaved changes · Save or reset on the filter row"
      >
        {chip}
      </Tooltip>
    ) : (
      chip
    )
  }

  return (
    <Box
      data-testid="views-bar"
      sx={{
        height: "32px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        overflow: "hidden",
      }}
    >
      {builtIns.map(renderBuiltInPill)}
      {/* Definitional views on the left, the user's own on the right. */}
      <Divider orientation="vertical" flexItem sx={{ marginY: "6px" }} />
      {visibleCustom.map(renderCustomPill)}
      {overflowCustom.length > 0 && (
        <>
          <Chip
            label={`${overflowCustom.length} more`}
            variant="outlined"
            clickable
            onClick={(event) => setOverflowAnchor(event.currentTarget)}
            onMouseEnter={(event) => setOverflowAnchor(event.currentTarget)}
            sx={focusRingSx}
          />
          <Popover
            open={overflowAnchor !== null}
            anchorEl={overflowAnchor}
            onClose={() => setOverflowAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            disableRestoreFocus
            slotProps={{
              paper: {
                onMouseLeave: () => setOverflowAnchor(null),
                sx: {
                  p: "8px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  maxWidth: "480px",
                },
              },
            }}
          >
            {overflowCustom.map(renderCustomPill)}
          </Popover>
        </>
      )}
      {/* Notion's "+ New view": names the current chip strip as a reusable
          view. One label in every state — a stable-width pill never resizes
          the calm views row. */}
      <Chip
        data-testid="save-view-pill"
        label="New view"
        clickable
        variant="outlined"
        icon={
          <SvgIcon
            component={appIcons.plus}
            inheritViewBox
            sx={{
              width: "16px",
              height: "16px",
              fill: "none",
              stroke: "currentColor",
            }}
          />
        }
        onClick={onNewView}
        sx={{ borderStyle: "dashed", ...focusRingSx }}
      />

      <Menu
        anchorEl={menuState?.anchorEl ?? null}
        open={menuState !== null}
        onClose={() => setMenuState(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuState) onRenameView(menuState.viewId)
            setMenuState(null)
          }}
        >
          <ListItemIcon sx={{ minWidth: "28px" }}>
            <MenuIcon icon={appIcons.edit03} />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuState) onDeleteView(menuState.viewId)
            setMenuState(null)
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ minWidth: "28px", color: "error.main" }}>
            <MenuIcon icon={appIcons.trash03} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  )
}
